import styled from '@emotion/styled';
import { useContext } from 'react';
import { AppTheme } from 'theme';
import { ThemeContext } from '@emotion/react';
import colors from 'lib/colors';
import { cover } from 'polished';

import { ReactComponent as LogoAnect } from '../assets/anect-logo.svg';
import { ReactComponent as LogoWithText } from '../assets/clashing-logo-currentColor.svg';
import { ReactComponent as LogoWithoutText } from '../assets/clashing-logo-without-text.svg';
import employeeBackgroundImage from '../assets/employee.png';
import hackerBackgroundImage from '../assets/hacker.png';
import employeeEduBackgroundImage from '../assets/employeeEdu2.png';
import hackerEduBackgroundImage from '../assets/hackerEdu2.png';

import { Flex, Footer } from 'components';

import LanguageSwitch from './LanguageSwitch';

interface Props {
  minHeight?: string;
  languageSwitch?: boolean;
}

const SimplePageLayout: React.FunctionComponent<Props> = ({
  children,
  minHeight,
  languageSwitch,
}) => {
  const theme = useContext(ThemeContext) as AppTheme;
  const IS_EDU = theme?.layout?.edu;

  return (
    <Container
      minHeight={minHeight}
      css={{
        background: IS_EDU
          ? `url(${hackerEduBackgroundImage}), url(${employeeEduBackgroundImage})`
          : `url(${hackerBackgroundImage}), url(${employeeBackgroundImage})`,
        backgroundSize: '35% auto, 35% auto',
        backgroundPosition: 'bottom left, bottom right',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed',
        color: colors.white,
      }}
    >
      <ClashingLogoWithText />
      {languageSwitch && (
        <LanguageSwitchWrapper>
          <LanguageSwitch right />
        </LanguageSwitchWrapper>
      )}
      <ContentContainer width={[0.8, 0.6]}>
        <ClashingLogoWithoutText />
        {children}
      </ContentContainer>
      <Footer>
        <AnectLogo />
      </Footer>
    </Container>
  );
};

export default SimplePageLayout;

const Container = styled(Flex)`
  ${cover()};
  justify-content: center;
  align-items: center;
  flex-direction: column;

  @media screen and (min-aspect-ratio: 21/9) {
    background-size: contain, contain;
  }

  @media screen and (max-width: 1000px), screen and (max-height: 600px) {
    background: none;
  }
`;

const ClashingLogoWithoutText = styled(LogoWithoutText)({
  marginBottom: '2em',
  '@media screen and (max-width: 1000px), screen and (max-height: 600px)': {
    display: 'none',
  },
});

const ClashingLogoWithText = styled(LogoWithText)({
  display: 'none',
  position: 'absolute',
  top: '32px',
  left: 'calc(50vw - 71px)',
  zIndex: 1,
  '@media screen and (max-width: 1000px), screen and (max-height: 600px)': {
    display: 'initial',
  },
});

const ContentContainer = styled(Flex)({
  position: 'relative',
  top: '-2em',
  textAlign: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  '@media screen and (max-width: 1000px), screen and (max-height: 600px)': {
    height: '100vh',
    width: '100vw',
    top: '0',
    paddingTop: '80px',
    paddingBottom: '80px',
    justifyContent: 'center',
  },
});

const AnectLogo = styled(LogoAnect)({
  display: 'none',
  '@media screen and (max-width: 1000px), screen and (max-height: 600px)': {
    display: 'initial',
  },
});

export const Subtitle = styled.p`
  font-size: 18px;
  letter-spacing: 0;
  text-align: center;
  line-height: 24px;
  color: white;
  margin: 16px 0;
  font-weight: 500;
  color: ${colors.lobbyText};
  @media screen and (max-width: 1000px), screen and (max-height: 600px) {
    font-size: 14px;
    margin: 32px 16px;
  }
`;

const LanguageSwitchWrapper = styled('div')({
  position: 'absolute',
  top: 0,
  right: 0,
  zIndex: 100,
});
